import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const err = (e, f) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : f);
const when = (v) => (v ? new Date(v).toLocaleString('en-AU') : '');

const SupportAccess = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ platform_admin_email: '', scope: 'read_only', reason: '', hours: 4 });
  const load = useCallback(() => api.get('/api/security/support-grants').then((r) => setRows(r.data.grants)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const grant = () => api.post('/api/security/support-grants', { ...f, hours: Number(f.hours) })
    .then(() => { toast.success('Support access granted'); load(); }).catch((e) => toast.error(err(e, 'Could not grant access')));
  const revoke = (id) => api.post(`/api/security/support-grants/${id}/revoke`).then(() => { toast.success('Access revoked'); load(); }).catch((e) => toast.error(err(e, 'Revoke failed')));
  return (
    <Card data-testid="support-access-card">
      <CardHeader><CardTitle>Platform support access</CardTitle>
        <CardDescription>Platform staff have no access to your data unless you grant it here. Access is time-limited (max 24h), fully audited and revocable.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-4 gap-2 items-end">
          <div className="sm:col-span-2"><Label>Platform admin email</Label><Input value={f.platform_admin_email} onChange={(e) => setF({ ...f, platform_admin_email: e.target.value })} data-testid="grant-email" /></div>
          <div><Label>Scope</Label><Select value={f.scope} onValueChange={(v) => setF({ ...f, scope: v })}><SelectTrigger data-testid="grant-scope"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="read_only">Read only</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>
          <div><Label>Hours (1–24)</Label><Input type="number" min={1} max={24} value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="grant-hours" /></div>
          <div className="sm:col-span-3"><Label>Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Investigate ticket #123" data-testid="grant-reason" /></div>
          <Button onClick={grant} disabled={!f.platform_admin_email || f.reason.length < 5} data-testid="grant-submit">Grant access</Button>
        </div>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">No support access has been granted.</p> : (
          <div className="divide-y border rounded-md text-sm">{rows.map((g) => (
            <div key={g.id} className="p-2 flex flex-wrap items-center gap-2" data-testid={`grant-row-${g.id}`}>
              <span className="font-medium">{g.platform_admin_email}</span><Badge variant="outline">{g.scope}</Badge>
              <Badge variant={g.status === 'active' ? 'secondary' : 'outline'} data-testid={`grant-status-${g.id}`}>{g.status}</Badge>
              <span className="text-xs text-muted-foreground flex-1">{g.reason} · until {when(g.expires_at)} · used {g.uses || 0}×</span>
              {g.status === 'active' && <Button size="sm" variant="outline" className="text-destructive" onClick={() => revoke(g.id)} data-testid={`grant-revoke-${g.id}`}>Revoke</Button>}
            </div>))}</div>)}
      </CardContent>
    </Card>
  );
};

const SecurityEvents = () => {
  const [rows, setRows] = useState([]);
  const [chain, setChain] = useState(null);
  const load = useCallback(() => api.get('/api/security/events').then((r) => setRows(r.data.events)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const verify = () => api.get('/api/security/audit/verify').then((r) => setChain(r.data)).catch((e) => toast.error(err(e, 'Verify failed')));
  return (
    <Card data-testid="security-events-card">
      <CardHeader><CardTitle>Security events</CardTitle>
        <CardDescription>Lockouts, MFA failures, new-device sign-ins, cross-organisation access attempts, unusual download volume and support access.</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={verify} data-testid="audit-verify-btn">Verify audit log integrity</Button>
          {chain && <span className={`text-sm ${chain.ok ? 'text-green-700' : 'text-destructive'}`} data-testid="audit-verify-result">
            {chain.ok ? `Audit chain intact (${chain.entries} entries)` : `Audit chain BROKEN: ${chain.problems.map((p) => `#${p.seq} ${p.problem}`).join('; ')}`}</span>}
        </div>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">No security events.</p> : (
          <div className="divide-y border rounded-md text-sm max-h-96 overflow-y-auto">{rows.map((e) => (
            <div key={e.id} className="p-2 flex flex-wrap items-center gap-2" data-testid={`security-event-${e.id}`}>
              <Badge variant={e.severity === 'high' ? 'destructive' : 'outline'}>{e.severity}</Badge>
              <span className="font-medium">{e.type.replace(/_/g, ' ')}</span>
              <span className="text-xs text-muted-foreground flex-1">{e.meta?.summary} · {when(e.created_at)}</span>
              {!e.acknowledged_at && <Button size="sm" variant="ghost" onClick={() => api.post(`/api/security/events/${e.id}/ack`).then(load)} data-testid={`security-event-ack-${e.id}`}>Acknowledge</Button>}
            </div>))}</div>)}
      </CardContent>
    </Card>
  );
};

export const SecurityAdminCards = ({ role }) => (
  <div className="space-y-6">
    {role === 'owner' && <SupportAccess />}
    <SecurityEvents />
  </div>
);
